/**
 * scoring.js
 * ----------
 * All score calculation logic for the Cawood Digital Maturity Assessment.
 *
 * This file is pure JavaScript — no React, no UI dependencies.
 * It takes raw question responses and returns structured results
 * that the ResultsStep component and pdfGenerator can consume directly.
 *
 * SCORING MODEL:
 *   Each domain has three questions, each rated 1–5 by the respondent.
 *   Raw domain score range: 3 (minimum) to 15 (maximum).
 *
 *   Raw score → Maturity level:
 *     3–5   → L1 (Unaware)
 *     6–8   → L2 (Emerging)
 *     9–10  → L3 (Developing)
 *     11–13 → L4 (Establishing)
 *     14–15 → L5 (Integrated)
 *
 * PERCEPTION GAP:
 *   Each domain also has a perception gap question rated on a qualitative scale.
 *   That scale maps to a numeric delta: -2, -1, 0, +1, +2.
 *   A delta of +1 or greater means the respondent scores their team higher than
 *   they believe the broader organisation would. This is flagged in the HubSpot note.
 *   A delta of -1 or lower means the broader organisation is perceived as ahead —
 *   also worth noting in the HubSpot conversation record.
 *
 * FOCUS DOMAIN:
 *   The respondent's I6 answer maps to a domain ID (D1–D6).
 *   That domain is surfaced first in results and marked as the primary focus area.
 *
 * COMBINED PROFILE:
 *   buildCombinedProfile() selects the two weakest and one strongest domain
 *   and assembles a combined narrative from WEAK_PAIRS and STRENGTH_ANCHORS
 *   in narratives.js. The result is added to the calculateResults() return object
 *   as combinedProfile. This is displayed as the "Your organisational profile"
 *   section in the results screen and PDF report.
 *
 *   Tie-breaking: equal scores are resolved by domain ID order (D1 before D2 etc.).
 *   Weak pair key format: two domain IDs joined by underscore, always alphabetical
 *   e.g. "D1_D3" regardless of which was weakest or second weakest.
 *
 * Connected files:
 *   - questions.js    provides DOMAINS and PERCEPTION_GAP_OPTIONS
 *   - narratives.js   provides NARRATIVES, WEAK_PAIRS, STRENGTH_ANCHORS
 *   - ResultsStep.jsx consumes the output of calculateResults()
 *   - pdfGenerator.js consumes the output of calculateResults()
 *   - hubspot.js      consumes the output of buildHubSpotNote()
 */

import { DOMAINS, INTAKE_QUESTIONS } from "../data/questions.js";
import { NARRATIVES, WEAK_PAIRS, STRENGTH_ANCHORS, PROFILE_NOUNS, PROFILE_ADJECTIVES } from "../data/narratives.js";

// ─── Constants ────────────────────────────────────────────────────────────────

// Maps raw domain scores to maturity level keys.
// Adjust these thresholds here if the scoring model changes in future.
const SCORE_TO_LEVEL = [
  { min: 14, level: "L5" },
  { min: 11, level: "L4" },
  { min:  9, level: "L3" },
  { min:  6, level: "L2" },
  { min:  3, level: "L1" },
];

// Maps the I6 "primary concern" option index to a domain ID.
// This order must match the options array in INTAKE_QUESTIONS[5] (I6).
const CONCERN_TO_DOMAIN = ["D1", "D2", "D3", "D4", "D5", "D6"];

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Maps a raw domain score (3–15) to a maturity level string (L1–L5).
 * @param {number} rawScore - Sum of three question responses for a domain.
 * @returns {string} Level key e.g. "L3"
 */
function getLevelFromScore(rawScore) {
  for (const { min, level } of SCORE_TO_LEVEL) {
    if (rawScore >= min) return level;
  }
  return "L1"; // fallback — should not be reached with valid input
}

/**
 * Converts a raw domain score to a 0–5 display scale.
 * Raw scores are 3–15. We normalise to 1–5 for consistent radar display.
 * Formula: ((rawScore - 3) / 12) * 5, rounded to one decimal place.
 * @param {number} rawScore
 * @returns {number} Display score between 1.0 and 5.0
 */
function normaliseScore(rawScore) {
  const normalised = ((rawScore - 3) / 12) * 5;
  return Math.round(normalised * 10) / 10;
}

/**
 * buildCombinedProfile()
 * ----------------------
 * Selects the two weakest and one strongest domain from processedDomains,
 * then assembles the combined profile narrative from WEAK_PAIRS and
 * STRENGTH_ANCHORS in narratives.js.
 *
 * The weak pair key is always alphabetical and underscore-joined:
 * e.g. "D1_D3" whether D1 or D3 was the lowest-scoring domain.
 *
 * Tie-breaking: equal display scores are resolved by domain ID order
 * (D1 before D2, D2 before D3, etc.).
 *
 * @param {Array} processedDomains - Output of the domain processing loop.
 * @returns {Object} combinedProfile
 *   {
 *     weakDomain1:  { id, label, score },  // lowest scoring domain
 *     weakDomain2:  { id, label, score },  // second lowest scoring domain
 *     strongDomain: { id, label, score },  // highest scoring domain
 *     weakPairKey:  "D1_D3",              // alphabetical key for WEAK_PAIRS lookup
 *     narrative:    "...",                // full assembled combined narrative
 *   }
 */
function buildCombinedProfile(processedDomains) {
  // Sort ascending by display score; tie-break by domain ID (alphabetical)
  const byScore = [...processedDomains].sort((a, b) => {
    if (a.displayScore !== b.displayScore) return a.displayScore - b.displayScore;
    return a.id.localeCompare(b.id);
  });

  const weak1  = byScore[0];
  const weak2  = byScore[1];
  const strong = byScore[byScore.length - 1];

  // Canonical weak pair key — always alphabetical regardless of score order
  const weakPairKey = [weak1.id, weak2.id].sort().join("_");

  const weakPairNarrative = WEAK_PAIRS[weakPairKey]       ?? WEAK_PAIRS.fallback;
  const strengthNarrative = STRENGTH_ANCHORS[strong.id]   ?? STRENGTH_ANCHORS.fallback;

  // Assemble the profile name: "[Strength adjective] [Weak pair noun]"
  // e.g. "The Operational Accumulator"
  // The noun already contains "The " — the adjective is inserted after "The ".
  const noun      = PROFILE_NOUNS[weakPairKey]     ?? PROFILE_NOUNS.fallback;
  const adjective = PROFILE_ADJECTIVES[strong.id]  ?? PROFILE_ADJECTIVES.fallback;
  const profileName = noun.replace(/^The /, `The ${adjective} `);

  return {
    weakDomain1:  { id: weak1.id,  label: weak1.label,  score: weak1.displayScore  },
    weakDomain2:  { id: weak2.id,  label: weak2.label,  score: weak2.displayScore  },
    strongDomain: { id: strong.id, label: strong.label, score: strong.displayScore },
    weakPairKey,
    profileName,
    narrative: `${weakPairNarrative}\n\n${strengthNarrative}`,
  };
}

// ─── Main export: calculateResults ───────────────────────────────────────────

/**
 * calculateResults()
 * ------------------
 * Takes all form responses and returns a complete results object.
 *
 * @param {Object} intakeAnswers     - Key/value pairs from the intake step.
 *                                     Keys are question IDs (I1–I6).
 * @param {Object} domainAnswers     - Key/value pairs from all domain questions.
 *                                     Keys are question IDs (D1.Q1, D1.Q2 etc.)
 * @param {Object} perceptionAnswers - Key/value pairs for perception gap questions.
 *                                     Keys are question IDs (D1.PG etc.)
 *                                     Values are numeric deltas (-2 to +2).
 *
 * @returns {Object} results - Structured results object (see shape below).
 *
 * RETURNED SHAPE:
 * {
 *   respondent: { fullName, firstName, lastName, email, organisation, headcount, sector },
 *   focusDomainId: "D2",
 *   domains: [
 *     {
 *       id: "D1",
 *       label: "Digital Strategy",
 *       color: "#FF6F61",
 *       rawScore: 9,
 *       displayScore: 2.5,
 *       level: "L3",
 *       levelLabel: "Developing",
 *       narrative: "...",
 *       perceptionDelta: 1,
 *       perceptionFlagged: true,
 *       isFocusDomain: false,
 *     },
 *     ...
 *   ],
 *   radarData: [
 *     { domain: "Digital Strategy", score: 2.5 },
 *     ...
 *   ],
 *   averageScore: 2.8,
 *   weakestDomain: { id, label },
 *   strongestDomain: { id, label },
 *   combinedProfile: {
 *     weakDomain1:  { id, label, score },
 *     weakDomain2:  { id, label, score },
 *     strongDomain: { id, label, score },
 *     weakPairKey:  "D1_D3",
 *     profileName:  "The Capable Accumulator",
 *     narrative:    "...",
 *   },
 *   questionSnapshots: [ ... ],
 * }
 */
export function calculateResults(intakeAnswers, domainAnswers, perceptionAnswers) {

  // ── Identify the focus domain from the I6 answer ──────────────────────────
  // I6 is a select field — its value is the index of the selected option.
  const concernIndex = intakeAnswers["I6"] ?? 0;
  const focusDomainId = CONCERN_TO_DOMAIN[concernIndex] || "D1";

  // ── Process each domain ───────────────────────────────────────────────────
  const processedDomains = DOMAINS.map((domain) => {

    // Sum the three question scores for this domain
    const questionIds = domain.questions.map((q) => q.id);
    const rawScore = questionIds.reduce((sum, qId) => {
      const answer = domainAnswers[qId];
      // Default to 1 if unanswered — prevents divide-by-zero and chart gaps
      return sum + (typeof answer === "number" ? answer : 1);
    }, 0);

    // Map raw score to maturity level
    const level = getLevelFromScore(rawScore);
    const narrativeEntry = NARRATIVES[domain.id]?.[level] ?? {};

    // Get perception gap delta for this domain
    const perceptionDelta = perceptionAnswers[domain.perceptionGap.id] ?? 0;

    // Flag if perception gap is significant
    const perceptionFlagged = Math.abs(perceptionDelta) >= 1;

    return {
      id:                 domain.id,
      label:              domain.label,
      color:              domain.color,
      rawScore,
      displayScore:       normaliseScore(rawScore),
      level,
      levelLabel:         narrativeEntry.levelLabel ?? level,
      narrative:          narrativeEntry.text ?? "",
      perceptionDelta,
      perceptionFlagged,
      isFocusDomain:      domain.id === focusDomainId,
    };
  });

  // ── Build radar chart data ─────────────────────────────────────────────────
  // Recharts RadarChart expects an array of objects with consistent keys.
  const radarData = processedDomains.map((d) => ({
    domain: d.label,
    score:  d.displayScore,
  }));

  // ── Aggregate metrics ─────────────────────────────────────────────────────
  const displayScores = processedDomains.map((d) => d.displayScore);
  const averageScore  = Math.round(
    (displayScores.reduce((a, b) => a + b, 0) / displayScores.length) * 10
  ) / 10;

  // Sort a copy — do not mutate processedDomains order
  const byScore         = [...processedDomains].sort((a, b) => a.displayScore - b.displayScore);
  const weakestDomain   = { id: byScore[0].id, label: byScore[0].label };
  const strongestDomain = { id: byScore[byScore.length - 1].id, label: byScore[byScore.length - 1].label };

  // ── Build combined profile ────────────────────────────────────────────────
  // Selects the two weakest and one strongest domain.
  // Assembles the organisational profile narrative from narratives.js.
  const combinedProfile = buildCombinedProfile(processedDomains);

  // ── Build respondent record ───────────────────────────────────────────────
  // Splits the full name into first/last for HubSpot mapping.
  const fullName  = intakeAnswers["I1"] ?? "";
  const nameParts = fullName.trim().split(/\s+/);
  const firstName = nameParts[0] ?? "";
  const lastName  = nameParts.slice(1).join(" ") ?? "";

  const respondent = {
    fullName,
    firstName,
    lastName,
    email:        intakeAnswers["I2"] ?? "",
    organisation: intakeAnswers["I3"] ?? "",
    headcount:    intakeAnswers["I4"] ?? "",
    sector:       intakeAnswers["I5"] ?? "",
  };

  // ── Build question snapshots for D1 analytics ────────────────────────────
  // Captures exact question text and answer value at time of submission.
  // Used by the Cloudflare Worker to write to the question_snapshots table.
  const questionSnapshots = DOMAINS.flatMap((domain) => {
    const scored = domain.questions.map((q) => ({
      domainId:     domain.id,
      questionId:   q.id,
      questionType: q.type,
      questionText: q.text,
      answerValue:  domainAnswers[q.id] ?? 1,
    }));
    const perception = {
      domainId:     domain.id,
      questionId:   domain.perceptionGap.id,
      questionType: "perception",
      questionText: domain.perceptionGap.text,
      answerValue:  perceptionAnswers[domain.perceptionGap.id] ?? 0,
    };
    return [...scored, perception];
  });

  return {
    respondent,
    focusDomainId,
    domains: processedDomains,
    radarData,
    averageScore,
    weakestDomain,
    strongestDomain,
    combinedProfile,
    questionSnapshots,
  };
}

// ─── HubSpot note builder ─────────────────────────────────────────────────────

/**
 * buildHubSpotNote()
 * ------------------
 * Generates the plain-text note that will be posted to the HubSpot
 * contact timeline via the Engagements API.
 *
 * The note is designed to be scannable by a consultant picking up the
 * contact for the first time. It surfaces the key conversation starters
 * without requiring them to open the PDF.
 *
 * @param {Object} results - The output of calculateResults()
 * @returns {string} Plain text note body
 */
export function buildHubSpotNote(results) {
  const { respondent, domains, focusDomainId, averageScore, combinedProfile } = results;
  const date = new Date().toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });

  // Build domain score lines, marking flagged perception gaps
  const domainLines = domains.map((d) => {
    const focusMarker  = d.isFocusDomain ? " ← respondent focus area" : "";
    const gapMarker    = d.perceptionFlagged
      ? ` [perception gap: ${d.perceptionDelta > 0 ? "+" : ""}${d.perceptionDelta}]`
      : "";
    const scoreDisplay = d.displayScore.toFixed(1).padStart(3);
    const levelPad     = d.levelLabel.padEnd(12);
    return `  ${d.label.padEnd(28)} ${scoreDisplay}/5   ${levelPad}${gapMarker}${focusMarker}`;
  });

  // Identify domains with flagged perception gaps for the summary section
  const flaggedDomains = domains.filter((d) => d.perceptionFlagged);
  const flaggedSummary = flaggedDomains.length
    ? flaggedDomains.map((d) =>
        `  ${d.label}: respondent scores team ${d.perceptionDelta > 0 ? "higher" : "lower"} than org (delta: ${d.perceptionDelta > 0 ? "+" : ""}${d.perceptionDelta})`
      ).join("\n")
    : "  No significant perception gaps identified.";

  // Combined profile summary line for the note
  const profileSummary = combinedProfile
    ? `  Profile:      ${combinedProfile.profileName}\n  Focus areas:  ${combinedProfile.weakDomain1.label} + ${combinedProfile.weakDomain2.label}\n  Strength:     ${combinedProfile.strongDomain.label}`
    : "  See domain scores above.";

  return `Digital Maturity Assessment — Lightweight v1
Completed: ${date}

RESPONDENT
  Name:         ${respondent.fullName}
  Organisation: ${respondent.organisation}
  Sector:       ${respondent.sector}
  Headcount:    ${respondent.headcount}

DOMAIN SCORES (out of 5)
${domainLines.join("\n")}

  Overall average: ${averageScore}/5

ORGANISATIONAL PROFILE
${profileSummary}

PERCEPTION GAPS
${flaggedSummary}

RECOMMENDED FOCUS
  ${domains.find((d) => d.isFocusDomain)?.label ?? "See domain scores above"}
  (Selected by respondent as primary concern — confirmed or contradicted by scores above)

NOTES FOR CONVERSATION
  - PDF report attached to this record
  - Assessment version: Lightweight v1
  - Full Cawood maturity assessment recommended as next step`;
}
